import React from "react";
import { motion } from "framer-motion";
import { Search, Code2, Cpu, LineChart, Rocket } from "lucide-react";

const steps = [
  {
    num: "01",
    icon: Search,
    title: "Discover",
    description: "We deeply analyse your workflows, pain points, and goals to map the highest-ROI automation opportunities.",
    color: "blue",
  },
  {
    num: "02",
    icon: Code2,
    title: "Build",
    description: "We design and build custom AI automation solutions using modern tech — n8n, Make, OpenAI, and custom APIs.",
    color: "violet",
  },
  {
    num: "03",
    icon: Cpu,
    title: "Automate",
    description: "We deploy your automations, integrate them with existing systems, and run thorough QA testing before go-live.",
    color: "blue",
  },
  {
    num: "04",
    icon: LineChart,
    title: "Optimize",
    description: "We monitor performance, gather data, and continuously tune your AI systems for maximum efficiency.",
    color: "violet",
  },
  {
    num: "05",
    icon: Rocket,
    title: "Scale",
    description: "Once automations are proven, we help you scale them across your entire organization.",
    color: "blue",
  },
];

export default function Process() {
  return (
    <section id="process" data-testid="process-section" className="py-24 md:py-36 relative overflow-hidden">
      <div className="section-divider" />

      {/* BG glow */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 50% 60%, rgba(139,92,246,0.05) 0%, transparent 60%)" }} />

      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12">
        {/* Header */}
        <div className="text-center max-w-xl mx-auto mb-20">
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="overline-badge mb-4">
            How It Works
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-[2.2rem] sm:text-[2.8rem] lg:text-[3.2rem] font-black tracking-[-0.04em] text-white leading-[1.05]"
          >
            From idea to{" "}
            <span className="gradient-text-static">live automation.</span>
          </motion.h2>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Horizontal connector — desktop */}
          <div className="hidden lg:block absolute top-[44px] left-[8%] right-[8%] h-px">
            <div className="w-full h-full" style={{ background: "linear-gradient(90deg, transparent, rgba(59,130,246,0.15) 15%, rgba(139,92,246,0.15) 50%, rgba(59,130,246,0.15) 85%, transparent)" }} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 lg:gap-2">
            {steps.map((step, i) => {
              const Icon = step.icon;
              const isBlue = step.color === "blue";
              return (
                <motion.div
                  key={step.num}
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.55, delay: i * 0.09, ease: [0.22, 1, 0.36, 1] }}
                  className="flex lg:flex-col items-start gap-5 lg:gap-0 lg:items-center lg:text-center group"
                >
                  {/* Node */}
                  <div className="relative flex-shrink-0">
                    {/* Pulse ring on hover */}
                    <div className={`absolute inset-0 rounded-full scale-150 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-lg ${isBlue ? "bg-blue-500/15" : "bg-violet-500/15"}`} />
                    <div className={`relative w-[88px] h-[88px] rounded-full flex items-center justify-center border transition-all duration-400 group-hover:scale-105 ${
                      isBlue
                        ? "bg-[#0E0E12] border-blue-500/20 group-hover:border-blue-500/45 group-hover:bg-blue-500/[0.07]"
                        : "bg-[#0E0E12] border-violet-500/20 group-hover:border-violet-500/45 group-hover:bg-violet-500/[0.07]"
                    }`}
                    style={{ boxShadow: `inset 0 1px 0 rgba(255,255,255,0.04)` }}>
                      <Icon className={`w-7 h-7 ${isBlue ? "text-blue-400/80" : "text-violet-400/80"}`} strokeWidth={1.25} />
                    </div>
                    {/* Step number */}
                    <span className="absolute -top-1 -right-1 text-[9px] font-mono text-white/20 bg-[#0E0E12] border border-white/[0.06] rounded px-1">
                      {step.num}
                    </span>
                  </div>

                  {/* Vertical connector mobile */}
                  {i < steps.length - 1 && (
                    <div className="lg:hidden w-px h-5 bg-white/[0.05] ml-11 my-0.5" />
                  )}

                  <div className="lg:mt-7 lg:px-3">
                    <h3 className="text-[14px] font-semibold text-white/85 mb-2 tracking-[-0.01em]">{step.title}</h3>
                    <p className="text-[12.5px] text-white/35 leading-[1.65]">{step.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
